import React, { useEffect, useState } from "react";
import { Box, Card, LinearProgress, Skeleton, Stack, Typography } from "@mui/joy";

import { fetchAdminSadhanaStats } from "../../api";

type SadhanaStats = {
  averageRounds?: number | null;
};

const AdminSadhanaStats: React.FC = () => {
  const [stats, setStats] = useState<SadhanaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminSadhanaStats()
      .then((data) => setStats(data ?? null))
      .catch(() => setError("We couldn't load the latest sadhana blossoms."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: "xl", p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="text" level="title-lg" width="50%" />
          <Skeleton variant="text" level="h1" width="30%" />
          <Skeleton variant="text" level="body-sm" width="70%" />
          <LinearProgress variant="soft" />
        </Stack>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined" sx={{ borderRadius: "xl", p: 3 }}>
        <Typography level="body-sm" color="danger">
          {error}
        </Typography>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card variant="outlined" sx={{ borderRadius: "xl", p: 3 }}>
        <Typography level="body-sm">Your first offering will create ripples of devotion.</Typography>
      </Card>
    );
  }

  const averageRounds =
    typeof stats.averageRounds === "number" && Number.isFinite(stats.averageRounds)
      ? stats.averageRounds
      : null;

  const progress = averageRounds === null ? 0 : Math.min(100, averageRounds * 4);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(231,244,255,0.88) 45%, rgba(236,248,255,0.78) 100%)",
      }}
    >
      <Stack spacing={0.75}>
        <Typography level="title-lg" sx={{ fontWeight: 700 }}>
          Collective chanting
        </Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 360 }}>
          Devotees are joyfully chanting together every day. Celebrate today’s average rounds below.
        </Typography>
      </Stack>
      {averageRounds !== null ? (
        <Box>
          <Typography level="h1" sx={{ fontWeight: 800 }}>
            {averageRounds.toFixed(1)}
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            Average rounds chanted daily across the community
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography level="title-md" sx={{ fontWeight: 600 }}>
            Chanting insights are on their way
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 420 }}>
            As soon as the next reports arrive, we’ll celebrate the community’s japa progress together.
          </Typography>
        </Box>
      )}
      <LinearProgress
        determinate={averageRounds !== null}
        value={progress}
        variant={averageRounds === null ? "soft" : undefined}
        sx={{ borderRadius: 999 }}
      />
    </Card>
  );
};

export default AdminSadhanaStats;
